import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ExcelService } from 'src/app/services/excel.service';
import { LotteryService, LotteryDrillDownRow, LotterySubsidyPattern } from 'src/app/services/lottery.service';
import { LotteryDrillDownContext } from '../lottery-dashboard/lottery-dashboard.component';
import { SubsidyPatternViewComponent } from '../subsidy-pattern-view/subsidy-pattern-view.component';

@Component({
  selector: 'app-lottery-division',
  templateUrl: './lottery-division.component.html',
  styleUrls: ['./lottery-division.component.scss']
})
export class LotteryDivisionComponent implements OnInit {

  @Input() drillData: LotteryDrillDownContext;
  @Output() closed = new EventEmitter<boolean>();

  divisions: LotteryDrillDownRow[] = [];
  filteredDivisions: LotteryDrillDownRow[] = [];
  loading = true;

  subDivisionView = false;
  selectedDivision: { id: number; name: string };

  showOnlyWithTarget = true;
  private searchText = '';

  totalTarget = 0;
  totalApplied = 0;
  totalAssigned = 0;
  totalBalanceTarget = 0;
  totalVillageCount = 0;

  private readonly excelColumns: string[] = [
    'Name', 'Lottery Executed Villages', 'Total Target', 'Applications Received', 'Benefitted Count', 'Balance Target',
    'Application Percentage', 'Benefitted Percentage'
  ];

  constructor(
    private excelService: ExcelService,
    private lotteryService: LotteryService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.lotteryService
      .getDrillDownList(
        this.drillData.financialYearId, this.drillData.departmentId, this.drillData.month,
        this.drillData.dbtGoId, this.drillData.dbtSchemeId, this.drillData.lotteryRunId, 0, 0, true)
      .subscribe((result: LotteryDrillDownRow[]) => {
        this.filteredDivisions = result || [];
        this.totalTarget = this.sumBy('totalTarget');
        this.totalApplied = this.sumBy('applicationsReceived');
        this.totalAssigned = this.sumBy('benefittedCount');
        this.totalBalanceTarget = this.sumBy('balanceTarget');
        this.totalVillageCount = this.sumBy('villageCount');
        this.applyFilters();
        this.loading = false;
      }, () => {
        this.loading = false;
      });
  }

  get pageHeading(): string {
    if (!this.drillData) {
      return '';
    }
    const schemeComponent = [this.drillData.schemeName, this.drillData.productName, this.drillData.goName, this.drillData.componentName]
      .filter(v => !!v).join(' | ');
    return `${this.drillData.departmentName || ''} | ${this.drillData.financialYearName || ''} | ${schemeComponent}`;
  }

  private sumBy(key: keyof LotteryDrillDownRow): number {
    return this.filteredDivisions.reduce((sum, d) => sum + Number(d[key]), 0);
  }

  private applyFilters(): void {
    let list = this.filteredDivisions;
    if (this.showOnlyWithTarget) {
      list = list.filter(d => Number(d.totalTarget) > 0);
    }
    if (this.searchText) {
      list = list.filter(d => d.name.trim().toLowerCase().startsWith(this.searchText));
    }
    this.divisions = list;
  }

  onTargetFilterChange(): void {
    this.applyFilters();
  }

  viewDivision(division: LotteryDrillDownRow): void {
    this.selectedDivision = { id: division.id, name: division.name };
    this.subDivisionView = true;
  }

  onSubDivisionClosed(): void {
    this.subDivisionView = false;
  }

  viewSubsidyPattern(division: LotteryDrillDownRow): void {
    this.openSubsidyPattern('District', division.name, division.id, 0, 0);
  }

  onCancel(): void {
    this.closed.emit(true);
  }

  viewOverallSubsidyPattern(): void {
    this.openSubsidyPattern('State', 'Tamil Nadu', 0, 0, 0);
  }

  private openSubsidyPattern(levelLabel: string, name: string, divisionId: number, subDivisionId: number, villageId: number): void {
    this.lotteryService.getSubsidyPattern(
      this.drillData.financialYearId, this.drillData.departmentId, this.drillData.month,
      this.drillData.dbtGoId, this.drillData.dbtSchemeId, this.drillData.lotteryRunId,
      divisionId, subDivisionId, villageId, true
    ).subscribe((result: LotterySubsidyPattern) => {
      this.dialog.open(SubsidyPatternViewComponent, {
        autoFocus: false,
        width: '900px',
        panelClass: 'mat-dialog-serviceschedule',
        data: {
          levelLabel, name,
          financialYear: this.drillData.financialYearName,
          goid: this.drillData.goName,
          scheme: this.drillData.schemeName,
          component: this.drillData.componentName,
          product: this.drillData.productName,
          remainingTarget: result?.remainingTarget ?? 0,
          remainingSubsidyAmount: result?.remainingSubsidyAmount ?? 0,
          rows: result?.rows ?? []
        }
      });
    });
  }

  onSearchChange(event): void {
    this.searchText = event.target.value.trim().toLowerCase();
    this.applyFilters();
  }

  exportAsXLSX(): void {
    const data = this.divisions.map(d => ({
      Name: d.name,
      LotteryExecutedVillages: d.villageCount,
      TotalTarget: d.totalTarget,
      ApplicationsReceived: d.applicationsReceived,
      BenefittedCount: d.benefittedCount,
      BalanceTarget: d.balanceTarget,
      ApplicationPercentage: d.applicationPercentage,
      BenefittedPercentage: d.benefittedPercentage
    }));
    this.excelService.exportAsExcelFile(data, 'Lottery Details', this.excelColumns);
  }
}
